import { useEffect, useRef, useState } from 'react';

/**
 * A <select> of the given options plus an "Other…" entry. When the user picks
 * "Other…" — or when `value` is a non-empty custom string not in `options` — a
 * text input renders beneath whose value IS the stored value, so an existing
 * custom value re-opens in the Other box.
 *
 * Genuinely self-contained styling-wise: it ships its own `<style>` block below.
 * It previously borrowed `.sub-field` / `.sub-label` / `.sub-input`, which are
 * declared in SubmittalsTab and (aliased onto `.rfi-*`) in RfisTab — so a shared
 * ui-kit component rendered correctly only inside one of two feature tabs, and
 * silently depended on two copies of three rules staying identical. Extracted
 * from SubmittalsTab so RFIs reuse it verbatim; the declarations here are those
 * rules unchanged, so both tabs render exactly as before.
 */
const css = `
  .swo-field { display: flex; flex-direction: column; }
  .swo-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px; }
  .swo-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark);
  }
`;

export interface SelectWithOtherProps {
  label?: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /**
   * Called with true while "Other…" is chosen but nothing has been typed yet, and
   * false otherwise (and on unmount).
   *
   * Choosing "Other…" emits '' — the typed text IS the value, so the old pick has
   * to go — and a '' that means "the user is about to type" is indistinguishable,
   * to a parent, from a '' that means "the user cleared this". A form that only
   * saves what changed would save the clear. Opt in to be told which it is, and
   * hold the save until the box is filled in (or another entry is picked).
   */
  onPendingChange?: (pending: boolean) => void;
}

const OTHER = 'Other…';

export default function SelectWithOther({
  label,
  options,
  value,
  onChange,
  placeholder,
  onPendingChange,
}: SelectWithOtherProps) {
  // "Other" mode when an explicit empty pick was made via Other, or the current
  // value is a non-empty string outside the option list.
  const isCustom = value !== '' && !options.includes(value);
  const [otherMode, setOtherMode] = useState(isCustom);
  const showOther = otherMode || isCustom;

  // Through a ref so a parent that passes a fresh function every render does not
  // re-fire this on every render.
  const pending = showOther && value === '';
  const report = useRef(onPendingChange);
  report.current = onPendingChange;
  useEffect(() => { report.current?.(pending); }, [pending]);
  useEffect(() => () => report.current?.(false), []);

  return (
    <div className="swo-field">
      <style>{css}</style>
      {label ? <label className="swo-label">{label}</label> : null}
      <select
        className="swo-input"
        value={showOther ? OTHER : value}
        onChange={(e) => {
          if (e.target.value === OTHER) {
            setOtherMode(true);
            onChange(''); // start blank; the text input drives the value
          } else {
            setOtherMode(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">{placeholder ?? 'Select…'}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
        <option value={OTHER}>{OTHER}</option>
      </select>
      {showOther && (
        <input
          className="swo-input"
          style={{ marginTop: 'var(--ew-s-2)' }}
          placeholder="Enter a value…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
        />
      )}
    </div>
  );
}
